import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import {
  Button,
  ButtonGroup,
} from "@datapay-by-datacom/design-system";
import { FiSearch } from "react-icons/fi";
import { ProfileSummary } from "../components";
import { useCompanyConfigs } from "../hooks";
import { COMPANY_CONFIG_BASE_PATH } from "./config";
import s from "./CompanyConfigRedesignScreen.module.css";

export function CompanyConfigRedesignScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const { data: configs, isLoading, isError } = useCompanyConfigs();
  const [query, setQuery] = useState("");
  const [selectedCode, setSelectedCode] = useState<string | null>(null);

  const needle = query.trim().toLowerCase();
  const filteredConfigs = useMemo(
    () =>
      configs?.filter((config) =>
        `${config.display_name} ${config.config_code}`
          .toLowerCase()
          .includes(needle)
      ) ?? [],
    [configs, needle]
  );

  useEffect(() => {
    if (filteredConfigs.length === 0) {
      if (selectedCode !== null) setSelectedCode(null);
      return;
    }
    if (
      !filteredConfigs.some(
        (config) => config.config_code === selectedCode
      )
    ) {
      setSelectedCode(filteredConfigs[0].config_code);
    }
  }, [filteredConfigs, selectedCode]);

  const selectedConfig =
    filteredConfigs.find((config) => config.config_code === selectedCode) ??
    filteredConfigs[0];
  const routeState = location.state as { statusMessage?: string } | null;

  return (
    <div className={s.container}>
      <header className={s.header}>
        <div className={s.headerText}>
          <h1 className={s.title}>Company configurations</h1>
          <p className={s.subtitle}>
            Find a configuration and review all options before maintaining or
            using it for onboarding.
          </p>
        </div>
        <Button
          variant="primary"
          onPress={() => navigate(`${COMPANY_CONFIG_BASE_PATH}/new`)}
        >
          New company configuration
        </Button>
      </header>

      {routeState?.statusMessage && (
        <p className={s.successStatus} role="status">
          {routeState.statusMessage}
        </p>
      )}
      {isLoading && (
        <p className={s.status} role="status">
          Loading company configurations…
        </p>
      )}
      {isError && (
        <div className={s.error} role="alert">
          Could not load company configurations.
        </div>
      )}
      {configs && configs.length === 0 && (
        <p className={s.empty}>
          No company configurations yet. Create the first one.
        </p>
      )}

      {configs && configs.length > 0 && (
        <div className={s.catalogue}>
          <aside className={s.cataloguePanel} aria-label="Configurations">
            <label className={s.search}>
              <span className={s.visuallyHidden}>Find a configuration</span>
              <FiSearch className={s.searchIcon} aria-hidden="true" />
              <input
                type="search"
                value={query}
                placeholder="Search by name or code"
                onChange={(event) => setQuery(event.target.value)}
              />
            </label>
            <p className={s.resultCount}>
              {filteredConfigs.length} configuration
              {filteredConfigs.length === 1 ? "" : "s"}
            </p>

            {filteredConfigs.length > 0 ? (
              <ul className={s.configList}>
                {filteredConfigs.map((config) => {
                  const isSelected =
                    config.config_code === selectedConfig?.config_code;
                  return (
                    <li key={config.config_code}>
                      <button
                        type="button"
                        className={`${s.configButton} ${
                          isSelected ? s.configButtonSelected : ""
                        }`}
                        aria-pressed={isSelected}
                        onClick={() => setSelectedCode(config.config_code)}
                      >
                        <span className={s.configName}>
                          {config.display_name}
                        </span>
                        <span className={s.configMeta}>
                          {config.config_code} · v{config.version}
                        </span>
                        <span
                          className={`${s.configStatus} ${
                            config.status === "RETIRED"
                              ? s.configStatusRetired
                              : s.configStatusPublished
                          }`}
                        >
                          {config.status.toLowerCase()}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p className={s.noMatches} role="status">
                No configurations match “{query.trim()}”.
              </p>
            )}
          </aside>

          {selectedConfig && (
            <>
              <p className={s.visuallyHidden} role="status">
                Selected {selectedConfig.display_name}, version{" "}
                {selectedConfig.version}, {selectedConfig.status.toLowerCase()}.
              </p>
              <section
                className={s.detail}
                aria-labelledby="selected-configuration-heading"
              >
              <header className={s.detailHeader}>
                <div>
                  <h2
                    id="selected-configuration-heading"
                    className={s.detailTitle}
                  >
                    {selectedConfig.display_name}
                  </h2>
                  <p className={s.detailMeta}>
                    {selectedConfig.config_code} · Version{" "}
                    {selectedConfig.version} ·{" "}
                    {selectedConfig.status.toLowerCase()}
                  </p>
                </div>
                <ButtonGroup>
                  <Button
                    variant="secondary"
                    onPress={() =>
                      navigate(
                        `${COMPANY_CONFIG_BASE_PATH}/${selectedConfig.config_code}/versions`
                      )
                    }
                  >
                    Version history
                  </Button>
                  <Button
                    variant="primary"
                    onPress={() =>
                      navigate(
                        `${COMPANY_CONFIG_BASE_PATH}/${selectedConfig.config_code}`
                      )
                    }
                  >
                    Maintain configuration
                  </Button>
                </ButtonGroup>
              </header>
              <ProfileSummary profile={selectedConfig.profile} />
              </section>
            </>
          )}
        </div>
      )}

    </div>
  );
}
